import * as React from 'react';
import PropTypes from 'prop-types';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import GppGoodIcon from '@mui/icons-material/GppGood';
import WarningIcon from '@mui/icons-material/Warning';
import { Checkbox, TableCell, TableRow, Tooltip } from '@mui/material';
import ORDER_STATUS from '../../../constants/orderStatus';
import CustomMenu from './CustomMenu/CustomMenu';
import toRusError from '../../../utils/toRusError';
import ColorBox from './styles/ColorBox';
import FinishWorkBox from './styles/FinishWorkBox';
import StatusBox from './styles/StatusBox';
import useTimeZone from '../../../../../../entities/timeZone/hooks/useTimeZone';
import convertUTCToLocalISOString from '../../../../../../shared/utils/convertUTCToLocalISOString';

const formatDateToLocalHours = (isoDate, utc) => {
  if (!isoDate || !utc) return '';

  return convertUTCToLocalISOString(isoDate, utc).slice(11, 16);
};

export default function VehicleRow({ row, isItemSelected, labelId, handleClick, setVehicleToDelete }) {
  const utc = useTimeZone();
  const workPeriod = `${formatDateToLocalHours(row.timePeriod?.timeWindowFrom, utc)} - ${formatDateToLocalHours(row.timePeriod?.timeWindowTo, utc)}`;

  const tankerSections = row.sections?.length;
  const trailerSections = row.trailer?.sections?.length;

  const sections =
    !tankerSections && !trailerSections
      ? '–'
      : tankerSections && trailerSections
        ? `${tankerSections} + ${trailerSections}`
        : `${tankerSections || trailerSections}`;

  return (
    <TableRow
      hover
      onClick={(event) => handleClick(event, row.id)}
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.registrationNum}
      selected={isItemSelected}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          checked={isItemSelected}
          inputProps={{
            'aria-labelledby': labelId,
          }}
        />
      </TableCell>
      <TableCell component="th" id={labelId} scope="row" padding="none">
        {row.registrationNum}
      </TableCell>
      <TableCell align="right">
        <ColorBox color={row.color} />
      </TableCell>
      <TableCell align="right">{formatDateToLocalHours(row.startWork, utc)}</TableCell>
      <TableCell align="right">
        <FinishWorkBox>
          {formatDateToLocalHours(row.finishWork, utc)}
          {row?.statuses?.includes(ORDER_STATUS.IS_NOT_IN_TIME) ? <WarningIcon sx={{ color: 'red' }} /> : null}
        </FinishWorkBox>
      </TableCell>
      <TableCell align="right">{`${row.garage?.name || ''}${row.garage?.address ? `, ${row.garage?.address}` : ''}`}</TableCell>
      <TableCell align="right">{`${row.averageSpeedInCity}/${row.averageSpeedOutsideCity}`}</TableCell>
      <TableCell align="right">{row.trailer?.registrationNum || '-'}</TableCell>
      <TableCell align="right">{sections}</TableCell>
      <TableCell align="right">{`${row.loadFactor}%`}</TableCell>
      <TableCell align="right">{workPeriod}</TableCell>
      <TableCell align="right">{row.mileage}</TableCell>
      <Tooltip
        arrow
        placement="left-end"
        title={
          row?.statuses?.length ? (
            row?.statuses?.includes('OK') ? null : (
              <div>
                {row?.statuses?.map(
                  (err, i) =>
                    i < 15 && (
                      <React.Fragment key={err}>
                        <p>
                          {i + 1} {err !== ORDER_STATUS.OK ? 'предупреждение' : ''} {err ? toRusError(err) : '-'}
                        </p>
                        {i === 14 ? <p>...</p> : ''}
                      </React.Fragment>
                    ),
                )}
              </div>
            )
          ) : null
        }
      >
        <TableCell align="left" sx={{ color: 'red' }}>
          {row?.statuses?.length ? (
            <StatusBox>
              {row?.statuses?.includes(ORDER_STATUS.OK) ? (
                <GppGoodIcon sx={{ color: 'green' }} />
              ) : (
                <>
                  {row?.statuses?.length} <ReportProblemIcon sx={{ color: 'red' }} />
                </>
              )}
            </StatusBox>
          ) : null}
        </TableCell>
      </Tooltip>
      <TableCell align="left">
        <CustomMenu row={row} onDelete={setVehicleToDelete} />
      </TableCell>
    </TableRow>
  );
}

VehicleRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string,
    registrationNum: PropTypes.string,
    color: PropTypes.string,
    startWork: PropTypes.string,
    finishWork: PropTypes.string,
    garage: PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.string,
    }),
    averageSpeedInCity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    averageSpeedOutsideCity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trailer: PropTypes.shape(),
    loadFactor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    timePeriod: PropTypes.shape({
      timeWindowFrom: PropTypes.string,
      timeWindowTo: PropTypes.string,
    }),
    statuses: PropTypes.arrayOf(PropTypes.string),
    mileage: PropTypes.number,
    sections: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  isItemSelected: PropTypes.bool.isRequired,
  labelId: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  setVehicleToDelete: PropTypes.func.isRequired,
};
